import { useCallback, useContext, useState } from "react";
import { PublicKey } from "@solana/web3.js";
import { useConnection } from "@solana/wallet-adapter-react";
import { AppConfigContext } from "../context";
import * as dripIdl from "../idl/drip_vault_v2.json";
import { DripVaultDescriptor } from "../constants/dripVaults";
import {
  DripPosition,
  dripProgramFor,
  fetchDripPositionCore,
  fetchSolPrice,
  solValueOf,
} from "../features/drip/dripFetch";
import { useDripVaults } from "./useDripVaults";

/**
 * The caller's position in every enabled vault, so the page can list them all
 * and offer a withdraw target per position.
 *
 * Prices every position — one SOL-price request for the whole pass, converted
 * per vault through solValueOf. useDripPosition only prices the vault the
 * deposit tab has selected, so without this an hSOL holder saw "—" beside a
 * real balance. Still skips the epoch enrichment: that needs a per-position
 * lookup the list does not use.
 */
export const useDripPositions = () => {
  const { connection } = useConnection();
  const { dripProgramAddress } = useContext(AppConfigContext);
  const vaults = useDripVaults();

  const [positions, setPositions] = useState<DripPosition[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  const fetchPositions = useCallback(async (publicKey: PublicKey) => {
    setIsLoading(true);

    try {
      const programId = new PublicKey(dripProgramAddress);
      const program = dripProgramFor(dripIdl, dripProgramAddress, connection);

      const [solPrice, results] = await Promise.all([
        fetchSolPrice(),
        Promise.all(
          vaults.map((vault: DripVaultDescriptor) =>
            fetchDripPositionCore(program, connection, vault, programId, publicKey)
          )
        ),
      ]);

      // Priced before filtering so each result still lines up with its vault,
      // whose kind decides how a token amount converts to SOL.
      //
      // An unavailable share supply leaves tokenValue at 0, which must not be
      // priced — "~$0.00" would read as an empty position.
      const priced = results.map((position, index) =>
        position === null
          ? null
          : {
              ...position,
              tokenValueUsd:
                solPrice != null && !position.isValueUnavailable
                  ? solValueOf(vaults[index], position.tokenValue, position.rate) * solPrice
                  : null,
            }
      );

      // Empty positions are noise in a list view. A position short of its claim
      // clamps to 0 but is not empty: dropping it would leave the wallet with no
      // explanation of where its deposit went.
      setPositions(
        priced.filter(
          (p): p is DripPosition => p !== null && (p.sharesRaw > 0 || p.isClaimShort)
        )
      );
    } catch (err) {
      console.error("Error fetching drip positions:", err);
      setPositions([]);
    } finally {
      setIsLoading(false);
    }
  }, [connection, dripProgramAddress, vaults]);

  return { positions, isLoading, fetchPositions };
};
